import { AreaPoints, WorkItemArea } from '../interfaces/worklog.interface'
import { AREA_META } from '../constants/worklog.constants'

//* Reparto de los puntos de una tarea entre las áreas que tocó (F-156). Espejo de las reglas de
//* `api-rendafin/src/modules/worklog/utils/area-split.util.ts`: el backend valida lo mismo, pero el
//* panel no deja confirmar un reparto que el backend rechazaría.

const uniq = <T>(xs: T[] | null | undefined): T[] => [...new Set(xs ?? [])]

/** ¿La tarea necesita reparto? Solo con dos o más áreas: con una es exacto por definición. */
export const needsSplit = (areas: string[] | null | undefined): boolean => uniq(areas).length >= 2

/** Suma de un reparto. */
export const splitSum = (split: AreaPoints | null | undefined): number => Object.values(split ?? {}).reduce((a, n) => a + (n ?? 0), 0)

/** Claves dentro de las áreas, enteros ≥0 y suma exacta de los puntos. Sin reparto que hacer, es válido. */
export function isValidSplit(points: number | null | undefined, areas: string[] | null | undefined, split: AreaPoints | null | undefined): boolean {
  if (!needsSplit(areas)) return true
  if (points == null || !split) return false
  const validAreas = uniq(areas)
  const entries = Object.entries(split)
  if (entries.some(([area, n]) => !validAreas.includes(area) || !Number.isInteger(n) || (n as number) < 0)) return false
  return splitSum(split) === points
}

/**
 * Partes iguales en enteros, el residuo para las primeras áreas (la primera es donde estuvo el
 * grueso): 13 entre dos son 7/6. Es el punto de partida del editor y el estimado de las métricas.
 */
export function equalSplit(points: number, areas: WorkItemArea[]): AreaPoints {
  const validAreas = uniq(areas)
  if (!validAreas.length) return {}
  const base = Math.floor(points / validAreas.length)
  let remainder = points - base * validAreas.length
  const out: AreaPoints = {}
  for (const a of validAreas) {
    out[a] = base + (remainder > 0 ? 1 : 0)
    if (remainder > 0) remainder--
  }
  return out
}

/** El reparto vigente si sigue cuadrando; si no, partes iguales. `null` cuando no aplica. */
export function seedSplit(points: number | null | undefined, areas: WorkItemArea[], current: AreaPoints | null | undefined): AreaPoints | null {
  if (points == null || !needsSplit(areas)) return null
  return current && isValidSplit(points, areas, current) ? { ...current } : equalSplit(points, areas)
}

/** Las claves de área del catálogo, como palabras sueltas: así una área nueva no se queda sin traducir. */
const AREA_KEY_PATTERN = new RegExp(`\\b(${Object.keys(AREA_META).join('|')})\\b`, 'g')

/** «Back 7 · Front 3» a partir de lo que guarda el backend («backend 7 · frontend 3») o de un reparto. */
export function splitLabel(value: string | AreaPoints | null | undefined): string {
  if (!value) return '—'
  if (typeof value === 'string') return value.replace(AREA_KEY_PATTERN, (a) => AREA_META[a as WorkItemArea].short)
  return (Object.keys(value) as WorkItemArea[]).map((a) => `${AREA_META[a]?.short ?? a} ${value[a]}`).join(' · ')
}
